import React, { FC, useEffect, useMemo, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";

import { TrainingDrillFolder } from "@/features/training/model/Training/TrainingDrillFolder";
import { TreeDrillFolderItem } from "@/features/training/pages/drill-tab/panel-left/accordion/TreeDrillFolderItem";
import TrainingDrillActions from "@/features/training/pages/drill-tab/panel-left/actions/TrainingDrillActions";
import { DrillsLeftPanelSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import { StyledTreeView } from "@/features/training/ui";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";

import { useFetchDrillFolders } from "@/service/TrainingQueries";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



export const DrillFoldersAccordion: FC = () => {
	const translation: Language | null = useContext(TranslationContext);
	const params = useParams<{
		drillId?: string;
		id?: string;
		sessionId?: string;
		drillSessionId?: string;
	}>();
	const drillId = params.drillId ?? params.id;
	const sessionId = params.sessionId ?? params.drillSessionId;
	const theme = useTheme();
	const [expanded, setExpanded] = useState<string[]>([]);
	const [selected, setSelected] = useState<string[]>([]);
	const [search, setSearch] = useState<string>("");
	const treeViewRef = useRef(null);
	const prevExpandedBeforeSearch = useRef<string[] | null>(null);
	const { isLoading, data: folders } = useFetchDrillFolders();

	useEffect(() => {
		if (!folders) return;

		// If URL has a drill id param, expand the folder containing that drill.
		// This works for both /training/drills/:drillId and
		// /training/drills/:drillId/sessions/:sessionId, and for drills that
		// live in either the Drills or Events collections.
		if (drillId != null) {
			let foundFolder: TrainingDrillFolder | undefined;
			let isEvent = false;

			for (const folder of folders) {
				if (folder.Drills?.some((y) => y.Id.toString() === drillId)) {
					foundFolder = folder;
					isEvent = false;
					break;
				}
				if (folder.Events?.some((e) => e.Id.toString() === drillId)) {
					foundFolder = folder;
					isEvent = true;
					break;
				}
			}

			if (foundFolder?.Id != null) {
				setExpanded([`drill-folder-${foundFolder.Id}`]);
			}

			// Highlight the correct node type (exercise vs event) in the tree
			const selectedKey = isEvent ? `event-${drillId}` : `exercise-${drillId}`;
			setSelected([selectedKey]);
		}
	}, [drillId, sessionId, folders]);

	// Scroll the selected item into view once expanded/selected (e.g. from deep links)
	useEffect(() => {
		if (selected.length === 0) return;
		const id = selected[0];
		const timeout = setTimeout(() => {
			const el = document.getElementById(`tree-node-${id}`);
			el?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
		}, 150);
		return () => clearTimeout(timeout);
	}, [selected, expanded]);

	const normalizedSearch = search.trim().toLowerCase();

	const filteredFolders: TrainingDrillFolder[] = useMemo(() => {
		if (!folders || !normalizedSearch) {
			return folders ?? [];
		}

		return folders
			.map((folder) => {
				const nameMatches =
					folder.Name?.toLowerCase().includes(normalizedSearch) ?? false;

				const filteredDrills =
					folder.Drills?.filter((d) =>
						d.Name?.toLowerCase().includes(normalizedSearch),
					) ?? [];

				const filteredEvents =
					folder.Events?.filter((e) =>
						e.Name?.toLowerCase().includes(normalizedSearch),
					) ?? [];

				if (
					!nameMatches &&
					filteredDrills.length === 0 &&
					filteredEvents.length === 0
				) {
					return null;
				}

				return {
					...folder,
					Drills: filteredDrills,
					Events: filteredEvents,
				};
			})
			.filter((f): f is TrainingDrillFolder => f !== null);
	}, [folders, normalizedSearch]);

	const hasSearch = normalizedSearch.length > 0;
	const hasResults = filteredFolders.length > 0;

	// Auto-expand folders that have matches while searching; restore on clear
	useEffect(() => {
		if (!hasSearch) {
			if (prevExpandedBeforeSearch.current !== null) {
				setExpanded(prevExpandedBeforeSearch.current);
				prevExpandedBeforeSearch.current = null;
			}
			return;
		}

		if (prevExpandedBeforeSearch.current === null) {
			prevExpandedBeforeSearch.current = expanded.slice();
		}

		const targetIds: string[] = [];
		for (const folder of filteredFolders) {
			if (folder.Id != null) {
				targetIds.push(`drill-folder-${folder.Id}`);
			}
		}

		if (targetIds.length === 0) return;

		setExpanded(targetIds);
	}, [expanded, hasSearch, filteredFolders]);

	if (isLoading || folders === undefined) {
		return (
			<Box
				component="section"
				className="accordion-wrapper"
				sx={{ flex: 1, minHeight: 0 }}
			>
				<DrillsLeftPanelSkeleton />
			</Box>
		);
	}

	if (folders.length === 0) {
		return (
			<Box
				component="section"
				className="accordion-wrapper"
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 2,
				}}
			>
				<Box
					sx={{
						textAlign: "center",
						p: 3,
						borderRadius: 2,
						border: "1px dashed",
						borderColor: "divider",
						width: "100%",
					}}
				>
					<FolderOutlinedIcon
						sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
					/>
					<Typography variant="body1" color="text.secondary" fontWeight={500}>
						<Translate String="Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.DrillFoldersAccordion.L195C74.NoFoldersYet">No folders yet</Translate>
					</Typography>
					<Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
						<Translate String="Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.DrillFoldersAccordion.L198C73.CreateAFolderToOrganizeYour">Create a folder to organize your drills.</Translate>
					</Typography>
				</Box>
			</Box>
		);
	}

	return (
		<Box
			component="section"
			className="accordion-wrapper"
			sx={{
				flex: 1,
				minHeight: 0,
				overflow: "hidden",
				display: "flex",
				flexDirection: "column",
			}}
		>
			{/* Toolbar with search + Actions */}
			<Stack
				direction="row"
				spacing={1}
				useFlexGap
				alignItems="center"
				justifyContent="space-between"
				sx={{
					flexShrink: 0,
					flexWrap: "wrap",
					px: 2,
					py: 1.75,
					bgcolor: "background.paper",
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}
			>
				<TextField
					size="small"
					placeholder={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.DrillFoldersAccordion.L238C18.SearchDrillFoldersDrillsEvents", "Search drill folders, drills & events…")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ flex: "1 1 240px", minWidth: 0 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" aria-hidden />
							</InputAdornment>
						),
						endAdornment:
							search.length > 0 ? (
								<InputAdornment position="end">
									<IconButton
										size="small"
										aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.DrillTab.PanelLeft.Accordion.DrillFoldersAccordion.L253C22.ClearSearch", "Clear search")}
										onClick={() => setSearch("")}
									>
										<span aria-hidden>✕</span>
									</IconButton>
								</InputAdornment>
							) : undefined,
					}}
				/>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						flexShrink: 0,
						flexWrap: "wrap",
						ml: "auto",
						justifyContent: "flex-end",
						width: { xs: "100%", sm: "auto" },
					}}
				>
					<TrainingDrillActions />
				</Box>
			</Stack>

			{/* Scrollable tree area – fills remaining space */}
			<Box
				className="wrapper-scroll"
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable",
					overscrollBehavior: "contain",
					py: 1,
					px: 1.25,
					"&::-webkit-scrollbar": { width: 8 },
					"&::-webkit-scrollbar-track": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.background.default, 0.5)
								: alpha(theme.palette.text.primary, 0.05),
						borderRadius: 8,
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.text.primary, 0.3)
								: alpha(theme.palette.text.primary, 0.2),
						borderRadius: 8,
					},
					"&:hover::-webkit-scrollbar-thumb": {
						backgroundColor:
							theme.palette.mode === "dark"
								? alpha(theme.palette.text.primary, 0.5)
								: alpha(theme.palette.text.primary, 0.35),
					},
				}}
			>
				{!hasResults && hasSearch ? (
					<Box
						sx={{
							p: 3,
							textAlign: "center",
							color: "text.secondary",
						}}
					>
						<Typography variant="body2" fontWeight={600}>
							No results found
						</Typography>
						<Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
							Try different keywords.
						</Typography>
					</Box>
				) : (
					<StyledTreeView
						aria-label="training drills navigator"
						defaultCollapseIcon={<ArrowDropDownOutlinedIcon />}
						defaultExpandIcon={<ArrowRightOutlinedIcon />}
						// v6 controlled API (bridged in StyledTreeView)
						// @ts-expect-error expandedItems is supported on SimpleTreeView v6
						expandedItems={expanded}
						// @ts-expect-error onExpandedItemsChange is supported on SimpleTreeView v6
						onExpandedItemsChange={(
							_event: React.SyntheticEvent,
							itemIds: string[],
						) => setExpanded(itemIds)}
						// v6 selection API – used to highlight active drill/session
						// @ts-expect-error selectedItems is supported on SimpleTreeView v6
						selectedItems={selected}
						// @ts-expect-error onSelectedItemsChange is supported on SimpleTreeView v6
						onSelectedItemsChange={(
							_event: React.SyntheticEvent,
							itemIds: string[],
						) => setSelected(itemIds)}
						ref={treeViewRef}
						sx={{
							flexGrow: 1,
							minHeight: 0,
						}}
					>
						{(hasSearch ? filteredFolders : folders)
							.filter((folder) => (folder.Drills?.length ?? 0) > 0)
							.map((folder, i) => (
								<TreeDrillFolderItem key={folder.Id} folder={folder} i={i} />
							))}
					</StyledTreeView>
				)}
			</Box>
		</Box>
	);
};
